import { useState, useEffect, useRef, useCallback } from "react";
import { Button, Input, Col, Row } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { API_BASE_URL } from "../../constants/api.constants";
import axios from "../../services/api/api";
import FoundUserInfo from "../UserManagement/FoundUserInfo";
import UserTypeIcon from "../Badges/UserTypeIcon";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import { compareStrings } from "../../Utilities/sortHelper";

const MAX_SUGGESTIONS = 50;

export default function FindUserInfo() {
    const intl = useIntl();
    const [foundUser, setFoundUser] = useState({});
    const [email, setEmail] = useState("");
    const [entries, setEntries] = useState([]);
    const [entriesLoaded, setEntriesLoaded] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [hasMoreMatches, setHasMoreMatches] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(-1);

    // refs till varje förslagsrad
    const suggestionRefs = useRef([]);
    // what has been typed so far, for when the list lands after the first keystrokes
    const latestNeedleRef = useRef("");

    // load the user list one time. Sorted here rather than in the query, so the server
    // does one plain scan; the search key is built here so typing filters on one
    // precomputed string per user instead of lowercasing two fields per keystroke
    useEffect(() => {
        const loadEntries = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/User/Lookup`);
                const list = Array.isArray(res.data?.result) ? res.data.result : [];
                setEntries(
                    list
                        .map((entry) => ({
                            ...entry,
                            // the server leaves out the type for students, who are nearly every row
                            userType: entry.userType ?? "Student",
                            key: `${entry.email} ${entry.name}`.toLowerCase(),
                        }))
                        .sort((a, b) => compareStrings(a.email, b.email))
                );
            } catch (err) {
                console.error("Kunde inte hämta användarlistan", err);
            } finally {
                setEntriesLoaded(true);
            }
        };
        loadEntries();
    }, []);

    const updateSuggestions = useCallback((needle, list) => {
        if (needle.length < 3) {
            setSuggestions([]);
            setHasMoreMatches(false);
            setShowSuggestions(false);
            return;
        }

        // stop at one past the cap: enough to know there are more, no need to scan the rest
        const matched = [];
        for (const entry of list) {
            if (entry.key.includes(needle)) {
                matched.push(entry);
                if (matched.length > MAX_SUGGESTIONS) break;
            }
        }
        setHasMoreMatches(matched.length > MAX_SUGGESTIONS);
        setSuggestions(matched.slice(0, MAX_SUGGESTIONS));
        setShowSuggestions(matched.length > 0);
    }, []);

    // the list usually lands after the first keystrokes; apply what has been typed so far,
    // but only while the field is still active - a search may already have been run
    useEffect(() => {
        if (entries.length > 0 && document.activeElement?.id === "user-search-email") {
            updateSuggestions(latestNeedleRef.current, entries);
        }
    }, [entries, updateSuggestions]);

    // scrolla aktiv rad in i vy när highlightIndex ändras
    useEffect(() => {
        if (highlightIndex < 0 || highlightIndex >= suggestions.length) return;
        const el = suggestionRefs.current[highlightIndex];
        if (el) {
            el.scrollIntoView({ block: "nearest" });
        }
    }, [highlightIndex, suggestions.length]);

    // sök-funktion – kan ta ett email direkt (t.ex. valt förslag)
    const searchUser = (emailOverride) => {
        const userEmail = (emailOverride ?? email).trim().toLowerCase();
        if (!userEmail) return;

        axios
            .get(
                `${API_BASE_URL}/User/ByEmail?email=${encodeURIComponent(
                    userEmail
                )}`
            )
            .then((response) => {
                var res = response?.data?.result;

                if (res) {
                    setFoundUser({
                        UserFound: true,
                        Id: res.id,
                        IsDeleted: res.isDeleted,
                        DeletedAt: res.deletedAt,
                        Created: res.created,
                        Name: res.name,
                        Email: res.email,
                        Type: res.userType,
                        Activated: res.hasActivatedAccount,
                        HasFullTeacherAccess: res.hasFullTeacherAccess,
                        LicenceOnSchool: res.licenceOnSchool,
                        Schools: res.schools,
                        Classes: res.classes,
                        ClassIds: res.classIds,
                        LicenceSchoolId: res.licenceSchoolId,
                        Licenses: res.coursesInLicense,
                        ProgramLicenses: res.licenses,
                        IsPurchaser: res.isPurchaser,
                        AplLicenseValidFrom: res.aplLicenseValidFrom,
                        AplLicenseValidTo: res.aplLicenseValidTo,
                        AplLicenseStatus: res.aplLicenseStatus,
                        LastSeen: res.lastSeen,
                        AplAppInfo: res.aplAppInfo,
                        ClassIsDeleted: res.classIsDeleted,
                        CanBeRestored: res.canBeRestored
                    });
                }
            })
            .catch((err) => {
                setFoundUser({
                    UserFound: false,
                    failMessage: formatServiceError(intl, err?.response?.data),
                });
            });
    };

    // handle typing + suggestions
    const onEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        setHighlightIndex(-1); // reset markering när man skriver

        const needle = value.trim().toLowerCase();
        latestNeedleRef.current = needle;
        updateSuggestions(needle, entries);
    };

    // when a suggestion is clicked
    const onSelectSuggestion = (entry) => {
        setEmail(entry.email);
        setSuggestions([]);
        setShowSuggestions(false);
        setHighlightIndex(-1);
    };

    const CheckUser = () => {
        if (foundUser.UserFound === false) {
            return <FoundUserInfo foundUser={foundUser} />;
        } else if (foundUser.UserFound === true) {
            return (
                <FoundUserInfo
                    foundUser={foundUser}
                    onUserChanged={() => searchUser(foundUser.Email)}
                />
            );
        } else {
            return null;
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            if (!showSuggestions || suggestions.length === 0) return;
            e.preventDefault();
            setHighlightIndex((prev) =>
                prev + 1 >= suggestions.length ? 0 : prev + 1
            );
            return;
        }

        if (e.key === "ArrowUp") {
            if (!showSuggestions || suggestions.length === 0) return;
            e.preventDefault();
            setHighlightIndex((prev) =>
                prev - 1 < 0 ? suggestions.length - 1 : prev - 1
            );
            return;
        }

        if (e.key === "Enter") {
            e.preventDefault();

            // 1) om ett förslag är markerat → sök på det, EN gång
            if (
                showSuggestions &&
                suggestions.length > 0 &&
                highlightIndex >= 0 &&
                highlightIndex < suggestions.length
            ) {
                const selected = suggestions[highlightIndex];
                setEmail(selected.email);
                setSuggestions([]);
                setShowSuggestions(false);
                setHighlightIndex(-1);

                // sök på det valda värdet (skicka in direkt → ingen state-racing)
                searchUser(selected.email);
                return;
            }

            // 2) ingen markering → sök på det som står i fältet
            if (email.trim() !== "") {
                searchUser();
            }
            return;
        }

        if (e.key === "Escape") {
            setShowSuggestions(false);
            setHighlightIndex(-1);
        }
    };

    return (
        <div className="container-fluid admin-container">
            <Row>
                <Col className="col-3 mb-2">
                    <h5 className="no-margin">
                        <FormattedMessage id="SearchUser" />
                    </h5>
                </Col>
            </Row>

            <Row className="search-row">
                <Col className="col-3" style={{ position: "relative" }}>
                    <Input
                        type="text"
                        id="user-search-email"
                        className={entriesLoaded ? undefined : "user-search-loading"}
                        placeholder={intl.formatMessage({
                            id: entriesLoaded ? "SearchEmailOrName" : "LoadingUsers",
                        })}
                        value={email}
                        onChange={onEmailChange}
                        onKeyDown={onKeyDown}
                        autoComplete="new-password"
                        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 120)}
                    />

                    {showSuggestions && suggestions.length > 0 && (
                        <div
                            style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                width: "max-content",
                                minWidth: "100%",
                                maxWidth: "80vw",
                                background: "white",
                                border: "1px solid #ccc",
                                zIndex: 15,
                                maxHeight: "200px",
                                overflowY: "auto"
                            }}
                        >
                            {suggestions.map((entry, i) => (
                                <div
                                    key={entry.email}
                                    ref={(el) => (suggestionRefs.current[i] = el)}
                                    onMouseDown={() => onSelectSuggestion(entry)}
                                    onMouseEnter={() => setHighlightIndex(i)}
                                    className={
                                        entry.notActivated && !entry.deleted ? "text-body-tertiary" : undefined
                                    }
                                    style={{
                                        padding: "6px 8px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #eee",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        background: i === highlightIndex ? "#eef6ff" : "white",
                                        textDecoration: entry.deleted ? "line-through" : "none"
                                    }}
                                >
                                    <UserTypeIcon userType={entry.userType} />
                                    {entry.email} — {entry.name}
                                </div>
                            ))}
                            {hasMoreMatches && (
                                <div style={{ padding: "6px 8px", color: "#777", fontStyle: "italic" }}>
                                    <FormattedMessage
                                        id="MoreMatchesTypeMore"
                                        values={{ count: MAX_SUGGESTIONS }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </Col>
            </Row>

            <Row>
                <Col className="col-3 mt-2">
                    <Button
                        color="primary"
                        outline
                        onClick={() => searchUser()}
                    >
                        <FormattedMessage id="Search" />
                    </Button>
                </Col>
            </Row>

            {CheckUser()}
        </div>
    );
}
